// frontend/src/hooks/useBlobDownload.js
//
// Fetch one file and hand it to the browser, with the busy state and the
// success/failure copy that every row-level download needs.
//
// The mechanics are identical wherever a grid row offers a file — a purchase
// order's PDF or items sheet, a recommendation's items sheet — and only the
// URL, the filename and the two i18n keys differ. The busy flag matters: a PO
// PDF is generated on demand and a big one takes seconds, and without it the
// row looks idle and a second click fetches the same file again.
//
// Not a TanStack mutation: there is no server state to invalidate and nothing
// to cache — the response is a blob that goes straight to disk.

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { downloadBlobResponse } from '../utils/download';
import { showToast } from '../components/common/appToastBus';

/**
 * @returns {{busy: boolean, download: Function}} `download(options)` takes
 *   `{ url, filename, failureKey, successKey, values }`, where `values` are the
 *   interpolation vars both messages are rendered with. A failure ends on the
 *   app toast, so the caller never catches.
 */
export function useBlobDownload() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const download = useCallback(async ({
    url, filename, failureKey, successKey, values,
  }) => {
    setBusy(true);
    try {
      const res = await sessionApi.get(url);
      // The thrown message is for the console, not the merchant: throwing the
      // translated copy would log "Failed to download PDF" and lose the status
      // that says WHY (400 on a draft, 409 on an ERP-owned PO's PDF).
      if (!res || !res.ok) throw new Error(`GET ${url} → ${res ? res.status : 'no response'}`);
      await downloadBlobResponse(res, filename);
      showToast(t(successKey, values), 'success');
    } catch (error) {
      console.error('download:', error);
      showToast(t(failureKey, values), 'error');
    } finally {
      setBusy(false);
    }
  }, [t]);

  return { busy, download };
}
